/**
 * Resolves who a record names — a user id, or an email written where a name
 * belongs — to the person's display name, for audit/provenance UI. Uses the
 * users table: name first, then email, then the id only if no better identity.
 */

import { useEffect, useState, useMemo } from 'react';
import { getSafeSupabaseClient } from '@/lib/supabaseSync';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isUuid(id: string): boolean {
  return UUID_REGEX.test(id);
}

/** An email address written where a person's name belongs (older plan authorizations did this). */
export function isEmailAddress(value: string): boolean {
  return EMAIL_REGEX.test(value.trim());
}

/**
 * Returns a map from actor/user ID (or email) to display string: name || email || id.
 * Other strings (e.g. 'unknown', 'system', a name) are returned as-is.
 * Empty or invalid IDs are not fetched.
 */
export function useActorDisplayMap(actorIds: (string | undefined | null)[]): Record<string, string> {
  const [users, setUsers] = useState<Array<{ id: string; display_name: string }>>([]);
  /** Lower-cased email → the person's name, for the emails that resolved. */
  const [byEmail, setByEmail] = useState<Record<string, string>>({});

  // One string per set of ids (and one per set of emails), so a new array with
  // the same values does not refetch.
  const { idKey, emailKey } = useMemo(() => {
    const ids = new Set<string>();
    const emails = new Set<string>();
    for (const v of actorIds) {
      if (!v) continue;
      if (isUuid(v)) ids.add(v);
      else if (isEmailAddress(v)) emails.add(v.trim().toLowerCase());
    }
    return { idKey: Array.from(ids).sort().join(','), emailKey: Array.from(emails).sort().join(',') };
  }, [actorIds]);

  useEffect(() => {
    const idsToFetch = idKey ? idKey.split(',') : [];
    if (idsToFetch.length === 0) {
      setUsers([]);
      return;
    }
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      setUsers([]);
      return;
    }
    // Use the SECURITY DEFINER RPC so RLS never blocks cross-user name lookups.
    supabase
      .rpc('resolve_user_display_names', { user_ids: idsToFetch })
      .then(({ data, error }) => {
        if (error) {
          console.warn('[useActorDisplayMap] RPC failed, falling back to direct query:', error.message);
          // Fallback: direct table query (may be blocked by RLS for some users)
          return supabase
            .from('users')
            .select('id, name, email')
            .in('id', idsToFetch)
            .then(({ data: fbData, error: fbError }) => {
              if (fbError) {
                console.warn('[useActorDisplayMap] Fallback query also failed:', fbError.message);
                setUsers([]);
              } else {
                setUsers(
                  (fbData as Array<{ id: string; name?: string | null; email: string }> ?? []).map(
                    (u) => ({ id: u.id, display_name: u.name?.trim() || u.email?.trim() || u.id })
                  )
                );
              }
            });
        }
        setUsers((data as Array<{ id: string; display_name: string }>) ?? []);
      });
  }, [idKey]);

  useEffect(() => {
    const emails = emailKey ? emailKey.split(',') : [];
    if (emails.length === 0) {
      setByEmail({});
      return;
    }
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      setByEmail({});
      return;
    }
    // The users table as the reader may see it (RLS: themselves and the people
    // they share a workspace with). An address nobody visible holds stays
    // unresolved, and the caller leaves the name out rather than print it.
    supabase
      .from('users')
      .select('id, name, email')
      .or(emails.map((e) => `email.ilike.${e}`).join(','))
      .then(({ data, error }) => {
        if (error) {
          console.warn('[useActorDisplayMap] email lookup failed:', error.message);
          setByEmail({});
          return;
        }
        const next: Record<string, string> = {};
        for (const u of (data as Array<{ id: string; name?: string | null; email?: string | null }>) ?? []) {
          const name = u.name?.trim();
          if (u.email && name) next[u.email.trim().toLowerCase()] = name;
        }
        setByEmail(next);
      });
  }, [emailKey]);

  return useMemo(() => {
    const map: Record<string, string> = {};
    for (const id of actorIds) {
      if (!id) continue;
      if (isUuid(id)) {
        const u = users.find((r) => r.id === id);
        map[id] = (u as { id: string; display_name?: string })?.display_name || id;
        continue;
      }
      if (isEmailAddress(id)) {
        map[id] = byEmail[id.trim().toLowerCase()] || id;
        continue;
      }
      map[id] = id;
    }
    return map;
  }, [actorIds, users, byEmail]);
}

/**
 * A person's name from a useActorDisplayMap map, or null while the name has not
 * arrived (the map holds the id or email itself until then) — so a line can
 * leave the name out instead of printing an id or an address.
 */
export function knownActorName(map: Record<string, string>, id: string | null | undefined): string | null {
  if (!id) return null;
  const name = map[id];
  return name && name !== id ? name : null;
}
